import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import HeaderLogo from "../../assets/images/header-logo.png";
import { ReactComponent as EarthImage } from "../../assets/Earth.svg";
import { ReactComponent as SplashLogo } from "../../assets/SplashLogo.svg";
import Temple1 from "../../assets/images/temple-1.png";
import Temple2 from "../../assets/images/temple-2.png";
import Temple3 from "../../assets/images/temple-3.png";
import Temple4 from "../../assets/images/temple-4.png";
import Temple5 from "../../assets/images/temple-5.png";
import { ReactComponent as TourGuideIcon } from "../../assets/tour-guide-icon.svg";
import { ReactComponent as CarIcon } from "../../assets/car-icon.svg";
import { ReactComponent as ClockIcon } from "../../assets/clock-icon.svg";
import { ReactComponent as StarIcon } from "../../assets/star-icon.svg";

const LandingPage = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate("/onboarding");
  };

  const features = [
    {
      icon: <TourGuideIcon className="w-12 h-12 text-orange1" />,
      title: "Expert Tour Guides",
      description: "Professional local guides with deep cultural knowledge and years of experience"
    },
    {
      icon: <CarIcon className="w-12 h-12 text-orange1" />,
      title: "Comfortable Transport",
      description: "Safe and comfortable vehicles with experienced drivers for all destinations"
    },
    {
      icon: <ClockIcon className="w-12 h-12 text-orange1" />,
      title: "Flexible Scheduling",
      description: "Choose your preferred travel time and customize your itinerary"
    },
    {
      icon: <StarIcon className="w-12 h-12 text-orange1" />,
      title: "Premium Experience",
      description: "Carefully curated experiences with attention to every detail"
    }
  ];

  const temples = [Temple1, Temple2, Temple3, Temple4, Temple5];

  const handleTempleClick = (index) => {
    const templeRoutes = [
      '/indore/iskcon',
      '/indore/devguradiatemple',
      '/indore/dewastemple',
      '/indore/gopaltemple',
      '/indore/janapavtemple'
    ];
    navigate(templeRoutes[index]);
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Aao Ghumen - Discover Amazing Places</title>
        <meta name="description" content="Experience the most beautiful temples, heritage sites, and cultural landmarks with our expert guides and premium travel services." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://aaoghumen.com/" />
      </Helmet>
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen bg-gradient-to-br from-orange1 to-[#FF7043] overflow-hidden">
        {/* Background Earth SVG */}
        <div className="absolute top-0 right-0 w-1/2 opacity-10">
          <EarthImage className="w-full" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
            <div className="text-center lg:text-left">
              <SplashLogo className="w-64 h-auto mx-auto lg:mx-0 mb-8" />
              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
                Discover Amazing <br />
                <span className="text-yellow-200">Places</span> with Us
              </h1>
              <p className="text-xl text-white/90 mb-8 max-w-lg">
                Experience the most beautiful temples, heritage sites, and cultural landmarks 
                with our expert guides and premium travel services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={handleGetStarted}
                  className="bg-white text-orange1 font-bold text-lg py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  Start Your Journey
                </button>
                <button
                  onClick={() => navigate("/about")}
                  className="border-2 border-white text-white font-bold text-lg py-4 px-8 rounded-xl hover:bg-white hover:text-orange1 transition-all duration-300"
                >
                  Learn More
                </button>
               
              </div>
            </div>
            <div className="relative flex flex-col items-center gap-6">
              <a
                href="https://play.google.com/store/apps/details?id=com.aao.ghumen"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex"
              >
                <img
                  src="/1.jpg"
                  alt="Download the Aao Ghumen app on Google Play"
                  className="h-[28rem] w-auto rounded-3xl shadow-2xl hover:shadow-[0_20px_45px_rgba(0,0,0,0.35)] transform hover:scale-105 transition-all duration-300"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.aao.ghumen"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-white/95 text-black font-semibold text-lg py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 mb-10"
              >
                <img
                  src="/playstore.png"
                  alt="Google Play icon"
                  className="h-8 w-12 object-contain"
                />
                <span className="">Download Now</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">About Aao Ghumen</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We are passionate about showcasing the rich cultural heritage and spiritual beauty 
              of temples and historical sites. Our mission is to provide unforgettable travel 
              experiences that connect you with local culture and history.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Story</h3>
              <p className="text-gray-600 mb-4">
                Founded with a vision to make cultural tourism accessible and enjoyable, Aao Ghumen 
                has been connecting travelers with the most beautiful and significant temples and 
                heritage sites across the region.
              </p>
              <p className="text-gray-600 mb-4">
                Our team of experienced guides and travel experts work tirelessly to ensure that 
                every journey is not just a trip, but a transformative experience that deepens 
                your understanding of local culture and spirituality.
              </p>
              <div className="grid grid-cols-3 gap-8 mt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange1">50+</div>
                  <div className="text-sm text-gray-600">Destinations</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange1">1000+</div>
                  <div className="text-sm text-gray-600">Happy Travelers</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange1">5★</div>
                  <div className="text-sm text-gray-600">Average Rating</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src={Temple1}
                alt="Beautiful temple"
                className="rounded-2xl shadow-xl w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Us</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the difference with our premium services and attention to detail
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-gray-50 hover:shadow-lg transition-shadow duration-300">
                <div className="flex justify-center mb-4">
                  <div className="bg-orange1/10 p-4 rounded-full">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Explore Our Destinations</h2>
            <p className="text-xl text-gray-600">
              Discover the beauty and spirituality of magnificent temples and heritage sites
            </p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {temples.map((temple, index) => (
              <div
                key={index}
                onClick={() => handleTempleClick(index)}
                className="relative group rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer"
              >
                <img
                  src={temple}
                  alt={`Temple ${index + 1}`}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 text-white">
                    <h4 className="font-semibold">Heritage Temple {index + 1}</h4>
                    <p className="text-sm text-gray-200">Cultural Site</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img src={HeaderLogo} alt="Aao Ghumen" className="h-8 w-auto mb-4 filter brightness-0 invert" />
              <p className="text-gray-400">
                Discover amazing places with expert guides and premium travel experiences.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="hover:text-white transition-colors">Home</button></li>
                <li><button onClick={() => navigate("/about")} className="hover:text-white transition-colors">About Us</button></li>
                <li><button onClick={() => navigate("/features")} className="hover:text-white transition-colors">Features</button></li>
                <li><button onClick={() => navigate("/gallery")} className="hover:text-white transition-colors">Gallery</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Destinations</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => navigate("/destinations")} className="hover:text-white transition-colors">All Destinations</button></li>
                <li><button onClick={() => navigate("/places")} className="hover:text-white transition-colors">All Places</button></li>
                <li><button onClick={() => navigate("/destinations/indore")} className="hover:text-white transition-colors">Indore</button></li>
                <li><button onClick={() => navigate("/destinations/ujjain")} className="hover:text-white transition-colors">Ujjain</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Legal & Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => navigate("/privacy-policy")} className="hover:text-white transition-colors">Privacy Policy</button></li>
                <li>Email: aaoghumen@gmail.com</li>
                <li>Phone: +91 97139 08893</li>
                <li>Support: 24/7 Available</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 relative">
            <p>&copy; 2025 Aao Ghumen. All rights reserved. Discover the world with us.</p>
            <div className="absolute bottom-2 right-4 text-xs text-gray-500">
              45 Pages v 1.0.0
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
